import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { GOOGLE_MAPS_API_KEY } from 'src/config-global';
import { useSettingsContext } from 'src/components/settings';
import { useAuthContext } from 'src/auth/hooks';
import useModelData from 'src/hooks/use-model-data';
import { DisplayPageData } from 'src/components/display-page-data';
import PageHeader from 'src/components/page-header';
import PageInfoCarousel from 'src/components/page-info-carousel';
import trackBoardImage from 'src/assets/images/route-buddy/track-board.png';
import trackFiltersImage from 'src/assets/images/route-buddy/track-filters.png';
import trackRowImage from 'src/assets/images/route-buddy/track-row.png';
import trackActionsImage from 'src/assets/images/route-buddy/track-actions.png';
import { Constants } from 'src/constants';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { DispatcherFleet, TrackRow, Trip } from '../types';
import { tripStatusText } from '../trip-utils';
import TrackBoard, { BoardLine, FleetScope } from './track-board';
import TripDrawer from './trip-drawer';

type PageModel = {
  board?: TrackRow[];
  myFleet?: DispatcherFleet | null;
};

type PendingAction = {
  trip: Trip;
  newStatus: number;
  driverName: string;
};

const actionText = (newStatus: number) => {
  if (newStatus === Constants.TRIP_STATUS_STARTED) {
    return 'start';
  }
  if (newStatus === Constants.TRIP_STATUS_FINISHED) {
    return 'finish';
  }
  return 'abandon';
};

const pageInfo = (
  <PageInfoCarousel
    steps={[
      {
        image: trackBoardImage,
        title: 'See all your drivers',
        text: 'Every row is one driver. The page updates every 20 seconds by itself. Late trips go to the top. The green dot means the driver is connected.',
      },
      {
        image: trackFiltersImage,
        title: 'Use the filters',
        text: "Use the round buttons above the list. 'Late' shows only trips behind schedule. The numbers show how many drivers are in each group. 'My Fleet' shows only your trucks.",
      },
      {
        image: trackRowImage,
        title: 'Open a trip',
        text: 'Click a row. You see the map, every stop, and the arrival times for that trip.',
      },
      {
        image: trackActionsImage,
        title: 'Start or finish a trip',
        text: 'Most trips start and finish alone. You can also do it by hand: use the buttons when you open a trip, or the small menu at the end of the row.',
      },
    ]}
  />
);

function flattenRow(row: TrackRow): BoardLine {
  const trip = row.currentTrip;
  const nextStop = trip ? trip.stops[trip.nextStopIndex] ?? null : null;
  return {
    eldDriverId: row.eldDriverId,
    driverName: `${row.firstName} ${row.lastName}`,
    unitNumber: row.eldVehicleUnitNumber,
    dutyStatus: row.dutyStatus,
    connected: row.connected,
    tripName: trip ? trip.name || `Trip #${trip.id}` : null,
    tripStatus: trip ? trip.status : null,
    progressPercent: trip ? trip.progressPercent : null,
    lateHours: trip ? trip.lateHours : null,
    nextStopText: nextStop ? nextStop.address : null,
    nextStopEta: trip?.etaNextStopAt ?? null,
    nextTripName: row.nextTrip ? row.nextTrip.name || `Trip #${row.nextTrip.id}` : null,
    nextTripDepartAt: row.nextTrip?.plannedDepartAt ?? null,
    docsText: (row.shippingDocs || []).join(' '),
    inMyFleet: row.inMyFleet,
    raw: row,
  };
}

function PageContent() {
  const settings = useSettingsContext();
  const { session } = useAuthContext();
  const timezone = (session?.organization?.companyTimezone as string) || 'America/Chicago';
  const router = useRouter();

  const dataLoader = useCallback(
    () => ({
      board: axios.get(endpoints.trips.trackBoard).then((response) => response.data),
      myFleet: axios
        .get(endpoints.dispatcherFleets.mine)
        .then((response) => response.data || null)
        .catch(() => null),
    }),
    []
  );
  const { data, error, loading, updateData } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    const interval = setInterval(() => {
      axios
        .get(endpoints.trips.trackBoard)
        .then((response) => updateData('board')(response.data))
        .catch(() => {
          // a failed background refresh keeps the last board; the next tick retries
        });
    }, 20_000);
    return () => clearInterval(interval);
  }, [updateData]);

  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [applyingAction, setApplyingAction] = useState(false);
  const [completingStop, setCompletingStop] = useState(false);

  const [fleetScope, setFleetScope] = useState<FleetScope>('all');
  const showFleetToggle = (data?.myFleet?.eldVehicleIds?.length ?? 0) > 0;

  // default a dispatcher with a fleet to their own trucks, but only once — the
  // 20s poll keeps rewriting data and must not undo a manual toggle
  const fleetScopeInitialized = useRef(false);
  useEffect(() => {
    if (fleetScopeInitialized.current || !data) {
      return;
    }
    fleetScopeInitialized.current = true;
    if (session?.isDispatcher && (data.myFleet?.eldVehicleIds?.length ?? 0) > 0) {
      setFleetScope('mine');
    }
  }, [data, session?.isDispatcher]);

  const lines = useMemo(() => (data?.board || []).map(flattenRow), [data?.board]);

  // derived from the freshest board so the open drawer live-updates with each poll
  const selectedRow = useMemo(
    () => data?.board?.find((row) => row.eldDriverId === selectedDriverId) || null,
    [data?.board, selectedDriverId]
  );

  const handleEditTrip = (trip: Trip) => {
    router.push(`${paths.app.routeBuddy.plan}?tripId=${trip.id}`);
  };

  // the dispatcher's way out when the geofence never captures an arrival
  const handleCompleteStop = async (trip: Trip, stopIndex: number) => {
    setCompletingStop(true);
    try {
      const response = await axios.post(
        formatEndpoint(endpoints.trips.completeStop, { tripId: trip.id, stopIndex })
      );
      const updatedTrip: Trip = response.data;
      updateData('board')(
        (data?.board || []).map((row) => {
          if (row.currentTrip?.id === updatedTrip.id) {
            const stillCurrent = updatedTrip.status === Constants.TRIP_STATUS_STARTED;
            return { ...row, currentTrip: stillCurrent ? updatedTrip : null };
          }
          return row;
        })
      );
      enqueueSnackbar(
        updatedTrip.status === Constants.TRIP_STATUS_FINISHED
          ? 'Stop marked done, trip finished'
          : 'Stop marked done'
      );
    } catch (requestError: any) {
      const validations = requestError?.response?.data;
      if (Array.isArray(validations)) {
        validations.forEach((validation: { message: string }) =>
          enqueueSnackbar(validation.message, { variant: 'error' })
        );
      } else {
        enqueueSnackbar('Could not mark the stop done', { variant: 'error' });
      }
    } finally {
      setCompletingStop(false);
    }
  };

  const applyStatusChange = async () => {
    if (!pendingAction) {
      return;
    }
    setApplyingAction(true);
    try {
      const response = await axios.post(
        formatEndpoint(endpoints.trips.updateStatus, { tripId: pendingAction.trip.id }),
        { status: pendingAction.newStatus }
      );
      const updatedTrip: Trip = response.data;
      updateData('board')(
        (data?.board || []).map((row) => {
          if (row.currentTrip?.id === updatedTrip.id) {
            const stillCurrent = updatedTrip.status === Constants.TRIP_STATUS_STARTED;
            return { ...row, currentTrip: stillCurrent ? updatedTrip : null };
          }
          if (row.nextTrip?.id === updatedTrip.id) {
            const nowStarted = updatedTrip.status === Constants.TRIP_STATUS_STARTED;
            return {
              ...row,
              currentTrip: nowStarted ? updatedTrip : row.currentTrip,
              nextTrip: null,
            };
          }
          return row;
        })
      );
      enqueueSnackbar(`Trip ${tripStatusText(updatedTrip.status).toLowerCase()}`, {
        variant: 'success',
      });
      setPendingAction(null);
    } catch (requestError: any) {
      const validations = requestError?.response?.data;
      if (Array.isArray(validations)) {
        validations.forEach((validation: { message: string }) =>
          enqueueSnackbar(validation.message, { variant: 'error' })
        );
      } else {
        enqueueSnackbar('Could not change the trip status', { variant: 'error' });
      }
    } finally {
      setApplyingAction(false);
    }
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Track" info={pageInfo} />

        <TrackBoard
          lines={lines}
          timezone={timezone}
          fleetScope={fleetScope}
          setFleetScope={setFleetScope}
          showFleetToggle={showFleetToggle}
          onSelectDriver={setSelectedDriverId}
          onEditTrip={handleEditTrip}
          onRequestStatusChange={(trip, newStatus, driverName) =>
            setPendingAction({ trip, newStatus, driverName })
          }
        />

        <TripDrawer
          row={selectedRow}
          timezone={timezone}
          completingStop={completingStop}
          onClose={() => setSelectedDriverId(null)}
          onEditTrip={handleEditTrip}
          onRequestStatusChange={(trip, newStatus, driverName) =>
            setPendingAction({ trip, newStatus, driverName })
          }
          onCompleteStop={handleCompleteStop}
        />

        {pendingAction && (
          <Dialog open onClose={applyingAction ? undefined : () => setPendingAction(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ textTransform: 'capitalize' }}>
              {actionText(pendingAction.newStatus)} this trip?
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {pendingAction.trip.name || `Trip #${pendingAction.trip.id}`} for{' '}
                {pendingAction.driverName}, planned to depart{' '}
                {DateTime.fromISO(pendingAction.trip.plannedDepartAt)
                  .setZone(timezone)
                  .toFormat('LLL d, h:mm a')}
                .
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingAction(null)} disabled={applyingAction}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={pendingAction.newStatus === Constants.TRIP_STATUS_ABANDONED ? 'error' : 'primary'}
                onClick={applyStatusChange}
                disabled={applyingAction}
                sx={{ textTransform: 'capitalize' }}
              >
                {actionText(pendingAction.newStatus)}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </DisplayPageData>
    </Container>
  );
}

export default function Page() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PageContent />
    </APIProvider>
  );
}
